import React, { useCallback, useEffect, useMemo, useState } from 'react';
import troubleTicketsService, {
  type TroubleTicket,
} from '../../../services/troubleTickets.service';
import TroubleTicketEditModal from './TroubleTicketEditModal';
import RepairOrderPingControls from '../../../components/RepairOrderPingControls';
import { useRepairOrderPings } from '../../../hooks/useRepairOrderPings';
import { repairOrderRecnoForTicket } from '../../../utils/repairOrderTicketLink';

export interface TroubleTicketsPanelProps {
  /** Pro number for the order. List/refetch is gated on this being defined. */
  pronumber: number | undefined;
  /** Optional billTo for THD category gating; passed through to the editor. */
  billTo?: number | null;
  /** Notifies the parent of the live count for the tab badge. */
  /** Total tickets shown, for the TTs tab badge. */
  onCountChange?: (count: number) => void;
  /** Open (NEW/PENDING) subset, for the tab's red flag. Reported separately
   *  because the panel lists RESOLVED and CANCELLED tickets too, so the badge
   *  and the flag are different numbers. */
  onOpenCountChange?: (count: number) => void;
  /**
   * Called after a successful ticket create with category="Reschedule
   * Requested". Parent uses this to re-fetch order details so cleared
   * driver/appt dates + new reschedule row appear immediately.
   */
  onRescheduleApplied?: () => void | Promise<void>;
  /** Called after the embedded service failure is created/updated, so the
   * parent refreshes its Service Failures count + panel. */
  onServiceFailureSaved?: () => void;
  /** True when the order has unsaved edits — blocks creating a Reschedule
   * Requested ticket (its reschedule side effect reads the saved order). */
  hasUnsavedOrderChanges?: boolean;
  /** Bumped by the parent to force a refetch — e.g. after the auto-triggered
   *  REJECTED trouble ticket is created from the leg-edit popup, which lives
   *  outside this panel so its onSaved can't refresh the list directly. */
  refreshNonce?: number;
}

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; ticket: TroubleTicket };

/** Format a backend datetime to mm/d/yy hh:mm (24h) — matches the legacy drawer. */
const formatTicketDate = (dt: string | null | undefined): string => {
  if (!dt) return '-';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/** Status → Tailwind badge classes (mirrors lines 2487-2493 of the legacy modal). */
const statusBadgeClass = (status: string | null | undefined): string => {
  const s = (status || '').toUpperCase();
  if (s === 'RESOLVED') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  if (s === 'NEW' || s === 'OPEN') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
  if (s === 'CANCELLED') return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  return 'bg-yellow-100 text-yellow-800 dark:bg-amber-900/40 dark:text-amber-300';
};

/**
 * Trouble Tickets list panel for the Pro 360 OrderFormView sub-tab.
 *
 * Mirrors the legacy `OrderDetailsModalContent` panel (lines 2448-2533) but
 * scoped to the Pro 360 layout — no absolute positioning, just a flex column
 * that fills the parent tab body. Clicking a row opens an editor MODAL
 * (not the legacy side drawer); "+ New" opens the same modal in create mode.
 */
const TroubleTicketsPanel: React.FC<TroubleTicketsPanelProps> = ({
  pronumber,
  billTo,
  onCountChange,
  onOpenCountChange,
  onRescheduleApplied,
  onServiceFailureSaved,
  hasUnsavedOrderChanges,
  refreshNonce = 0,
}) => {
  const [tickets, setTickets] = useState<TroubleTicket[]>([]);
  // Start in the loading state when there's a pro to fetch, so the spinner
  // shows immediately on first mount instead of the "No trouble tickets" empty
  // state flashing for a frame before the fetch effect runs.
  const [loading, setLoading] = useState<boolean>(() => pronumber != null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });

  const fetchTickets = useCallback(
    async (signal?: AbortSignal) => {
      if (!pronumber) {
        setTickets([]);
        onCountChange?.(0);
        onOpenCountChange?.(0);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { tickets: list, openCount } =
          await troubleTicketsService.listByPronumberWithOpenCount(pronumber, signal);
        if (signal?.aborted) return;
        setTickets(list);
        onCountChange?.(list.length);
        onOpenCountChange?.(openCount);
      } catch (err: any) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        // eslint-disable-next-line no-console
        console.error('Failed to fetch trouble tickets:', err);
        setError(err?.response?.data?.detail || err?.message || 'Failed to load tickets');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [pronumber, onCountChange, onOpenCountChange],
  );

  // Initial + pronumber-change fetch; also refetches when the parent bumps
  // refreshNonce (e.g. after the leg-edit REJECTED popup creates a ticket).
  useEffect(() => {
    const controller = new AbortController();
    fetchTickets(controller.signal);
    return () => controller.abort();
  }, [fetchTickets, refreshNonce]);

  const handleSaved = useCallback(() => {
    // Refetch on success so the row reflects new server-side fields
    // (datetime, userid, resolve, etc.) and the count badge stays accurate.
    void fetchTickets();
  }, [fetchTickets]);

  const closeModal = useCallback(() => setModal({ kind: 'closed' }), []);

  // Every repair order this panel's tickets refer to, found by parsing
  // `Repair Order #<n>` out of each REPAIR ORDER ticket's issue text — the only
  // link there is (troubleticket has no repairorderrecno, and pronumber cannot
  // say WHICH of a PRO's repair orders a ticket is about). Hand-written tickets
  // parse to null and simply get no controls.
  const ticketRoRecnos = useMemo(
    () => tickets.map((t) => repairOrderRecnoForTicket(t)),
    [tickets],
  );
  // One batched lookup for the whole panel — never one per card.
  const roRecnos = useMemo(
    () => ticketRoRecnos.filter((r): r is number => r !== null),
    [ticketRoRecnos],
  );
  const { pingsByRo, cardsByRo, requestPing } = useRepairOrderPings(pronumber, roRecnos);
  // The first ticket that actually RENDERS controls — a parsed recno whose
  // repair order came back — so permissions mode decorates a handle that exists.
  const firstRoTicketIndex = ticketRoRecnos.findIndex((r) => r !== null && !!cardsByRo[r]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-amber-600"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-semibold text-body">Trouble Tickets</span>
          {tickets.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {tickets.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'create' })}
          disabled={!pronumber}
          className="px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-700 dark:hover:bg-amber-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="New trouble ticket"
        >
          + New
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-4" role="status" aria-label="Loading">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600" />
            <span className="text-sm text-gray-400 dark:text-gray-500">Loading…</span>
          </div>
        ) : error ? (
          <div className="py-4 text-center text-xs text-red-600 dark:text-red-300">{error}</div>
        ) : tickets.length === 0 ? (
          <div className="flex items-center justify-center py-4 text-sm text-gray-400 dark:text-gray-500">
            No trouble tickets
          </div>
        ) : (
          <div className="space-y-1.5">
            {tickets.map((ticket, index) => {
              const statusUpper = (ticket.status || 'N/A').toUpperCase();
              const roRecno = ticketRoRecnos[index];
              return (
                <div
                  key={ticket.recno}
                  role="button"
                  tabIndex={0}
                  onClick={() => setModal({ kind: 'edit', ticket })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setModal({ kind: 'edit', ticket });
                    }
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 bg-panel hover:bg-highlight dark:border-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-medium text-body truncate"
                        title={ticket.category || 'No Category'}
                      >
                        {ticket.category || 'No Category'}
                      </span>
                      <span
                        className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium ${statusBadgeClass(ticket.status)}`}
                      >
                        {statusUpper}
                      </span>
                      {ticket.driverticket === 'Y' && (
                        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                          Driver
                        </span>
                      )}
                    </div>
                    {/* No card for the parsed recno — a deleted repair order,
                        or one belonging to another PRO — means we know nothing
                        about it, so render nothing rather than guess. */}
                    {roRecno !== null && cardsByRo[roRecno] && (
                      <RepairOrderPingControls
                        pings={pingsByRo[roRecno]}
                        allowRequest
                        orderStatus={cardsByRo[roRecno].orderStatus}
                        onRequest={(requestType) => { void requestPing(roRecno, requestType); }}
                        // Repeated instances share one componentId; decorate
                        // only the first repair-order ticket in permissions mode.
                        showHandle={index === firstRoTicketIndex}
                      />
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[0.625rem] text-muted">
                        {formatTicketDate(ticket.datetime)}
                      </span>
                      <span className="text-[0.625rem] text-gray-400 dark:text-gray-500">
                        by {ticket.userid || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal.kind !== 'closed' && pronumber !== undefined && (
        <TroubleTicketEditModal
          pronumber={pronumber}
          ticket={modal.kind === 'edit' ? modal.ticket : undefined}
          billTo={billTo ?? undefined}
          onClose={closeModal}
          onSaved={handleSaved}
          onRescheduleApplied={onRescheduleApplied}
          onServiceFailureSaved={onServiceFailureSaved}
          hasUnsavedOrderChanges={hasUnsavedOrderChanges}
        />
      )}
    </div>
  );
};

export default TroubleTicketsPanel;
